import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { map } from 'lodash-es';
import { QueryOptions } from '~/api/types';
import { SERVER_URL } from '~/config';
import { IShareLink, IShareLinkCreateRequest, ShareLink, ShareLinkRefType } from '~/models/share';
import { useHttpClient } from './base';

const BASE_URL = '/shares';

export function useAllShareLinks(
  refType: ShareLinkRefType,
  refId: string
): UseQueryResult<ShareLink[] | null, unknown> {
  const client = useHttpClient(BASE_URL);
  return useQuery(['shareLinks', 'all'], async () => {
    const { data } = await client.get<IShareLink[]>('all', { params: { refId, refType } });
    return map(data, (it) => ShareLink.from(it));
  });
}

export function useCreateShareLink(): UseMutationResult<
  IShareLink,
  unknown,
  IShareLinkCreateRequest & { refType: ShareLinkRefType; refId: string },
  unknown
> {
  const client = useHttpClient(BASE_URL);
  const queryClient = useQueryClient();
  return useMutation(
    async ({ refId, refType, ...shareLinkCreateRequest }) => {
      const { data } = await client.post<IShareLink>('', shareLinkCreateRequest, { params: { refId, refType } });
      return data;
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['shareLinks']);
      }
    }
  );
}

export function useDeleteShareLink(): UseMutationResult<
  void,
  unknown,
  { id: string; refType: ShareLinkRefType; refId: string },
  unknown
> {
  const client = useHttpClient(BASE_URL);
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, refId, refType }) => {
      await client.delete(id, { params: { refId, refType } });
    },
    {
      onSuccess: async (_, id) => {
        await Promise.all([
          queryClient.invalidateQueries(['shareLink', id]),
          queryClient.invalidateQueries(['shareLinks'])
        ]);
      }
    }
  );
}

export function useShareLink(sid: string, pwd: string, options?: QueryOptions): UseQueryResult<ShareLink, unknown> {
  const client = axios.create({
    baseURL: SERVER_URL + '/shares'
  });
  return useQuery(
    ['shareLink', sid],
    async () => {
      const { data } = await client.get<IShareLink>(`${sid}/report`, {
        headers: { 'X-Password': pwd }
      });
      return ShareLink.from(data);
    },
    options
  );
}
